'use client';

import { useTranslation } from 'react-i18next';
import {
  useWikiSource,
  ContentModuleShell,
  type WikiTreeNode,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { WikiTreeNav } from './WikiTreeNav';
import { WikiTreeBlock } from '../wiki/WikiTreeBlock';

export interface WikiIndexLayoutProps {
  title?: string;
  config?: {
    showSearch?: boolean;
    expandTreeToDepth?: number;
  };
  pageHrefTemplate?: string;
  /** Server-fetched tree for SSR. When provided, the layout renders it
   *  directly (no client WikiSource fetch) so /wiki is server-rendered. */
  tree?: WikiTreeNode[];
}

export function WikiIndexLayout({
  title,
  config,
  pageHrefTemplate = '/wiki/{slug}',
  tree: treeProp,
}: WikiIndexLayoutProps) {
  const { t } = useTranslation('site-builder-blocks');
  const heading = title ?? t('shared.label.documentation');
  const wiki = useWikiSource();
  const state = useAsyncResource(async () => {
    if (treeProp !== undefined) return null; // ignored when prop provided
    if (!wiki) return null;
    const tree = await wiki.getTree();
    return tree.length > 0 ? tree : null;
  }, [wiki, treeProp]);

  // Prop path (SSR) resolves synchronously; client path uses the resource.
  const tree: WikiTreeNode[] | null | undefined =
    treeProp !== undefined
      ? treeProp.length > 0
        ? treeProp
        : null
      : state.kind === 'loaded'
        ? state.data
        : undefined;

  if (treeProp === undefined && !wiki) {
    return <AdapterPlaceholder blockType="WikiIndexLayout" adapterName="WikiSource" />;
  }

  if (tree === undefined || tree === null) return null;

  return (
    <ContentModuleShell
      title={heading}
      sidebar={
        <WikiTreeNav
          tree={tree}
          expandToDepth={config?.expandTreeToDepth ?? 1}
          pageHrefTemplate={pageHrefTemplate}
        />
      }
    >
      <WikiTreeBlock
        title={title}
        tree={tree}
        layout="columns"
        pageHrefTemplate={pageHrefTemplate}
      />
    </ContentModuleShell>
  );
}
